import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input component props extending standard input attributes
 * We omit the native 'prefix' prop as we override it with our custom prefix element
 */
export interface InputProps extends Omit<React.ComponentProps<"input">, "prefix"> {
  /** Optional prefix element (e.g., icon) - renders before the input */
  startAdornment?: React.ReactNode;
  /** Optional suffix element (e.g., clear button) - renders after the input */
  endAdornment?: React.ReactNode;
}

/**
 * Basic Input component
 *
 * A styled input field with optional start/end adornments.
 * Follows the design system's styling conventions.
 *
 * @example
 * ```tsx
 * <Input placeholder="Enter text..." />
 *
 * <Input
 *   startAdornment={<Search className="h-4 w-4" />}
 *   placeholder="Search..."
 * />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", startAdornment, endAdornment, ...props }, ref) => {
    // If no adornments, render simple input
    if (!startAdornment && !endAdornment) {
      return (
        <input
          type={type}
          data-slot="input"
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
            "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          ref={ref}
          {...props}
        />
      )
    }

    // With adornments, wrap in container
    return (
      <div
        data-slot="input-wrapper"
        className={cn(
          "relative flex h-10 w-full items-center rounded-md border border-input bg-background text-sm",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
          className
        )}
      >
        {startAdornment && (
          <div
            data-slot="input-start-adornment"
            className="pointer-events-none flex items-center pl-3 text-muted-foreground"
          >
            {startAdornment}
          </div>
        )}
        <input
          type={type}
          data-slot="input"
          className={cn(
            "flex-1 h-full bg-transparent py-2 text-sm",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none",
            "disabled:cursor-not-allowed",
            startAdornment ? "pl-2" : "pl-3",
            endAdornment ? "pr-2" : "pr-3"
          )}
          ref={ref}
          {...props}
        />
        {endAdornment && (
          <div
            data-slot="input-end-adornment"
            className="flex items-center pr-1.5 text-muted-foreground"
          >
            {endAdornment}
          </div>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
